import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "../common/Logo";

const INTRO_SRC = "/assets/homehero/intro.mp4";
const HERO_SRC = "/assets/homehero/hero-proxy.mp4";
const INTRO_POSTER = "/assets/homehero/intro-poster.webp";
const HERO_POSTER = "/assets/homehero/hero-poster.webp";

const INTRO_MS = 5000; // stage-one video runs ~5s
const FADE_MS = 800; // fade-to-black duration (600–900ms)
const PIN_VH = 300;

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const emitReveal = (revealed) => window.dispatchEvent(new CustomEvent("kv:hero-reveal", { detail: { revealed } }));
const emitNav = (opacity) => window.dispatchEvent(new CustomEvent("kv:hero-nav", { detail: { opacity } }));

// Native colour preserved — only a light gradient for text legibility (no tint, no grade).
const OVERLAY = "linear-gradient(to bottom, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.08) 42%, rgba(0,0,0,0.56) 100%)";

const SCOPED_CSS = `
@keyframes kvHeroBreathe { 0%,100% { opacity: .35 } 50% { opacity: .8 } }
@keyframes kvHeroGlow { 0%,100% { filter: drop-shadow(0 0 14px rgba(255,255,255,0.28)) } 50% { filter: drop-shadow(0 0 26px rgba(255,255,255,0.5)) } }
.kv-hero-breathe { animation: kvHeroBreathe 3.4s ease-in-out infinite; }
.kv-hero-glow { animation: kvHeroGlow 4.5s ease-in-out infinite; }
`;

export default function HomeHero() {
  const [phase, setPhase] = useState("intro"); // intro | black | hero
  const [introRunId, setIntroRunId] = useState(0);
  const phaseRef = useRef("intro");
  const introVideoRef = useRef(null);
  const heroVideoRef = useRef(null);
  const rootRef = useRef(null);
  const logoRef = useRef(null);
  const promptRef = useRef(null);
  const kickerRef = useRef(null);
  const headlineRef = useRef(null);
  const bodyRef = useRef(null);
  const ctaRef = useRef(null);
  const introTimers = useRef([]);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const skipIntro = () => {
    introTimers.current.forEach(clearTimeout);
    introTimers.current = [];
    setPhase("hero");
    window.scrollTo(0, 0);
  };

  // Replay the whole cinematic intro (triggered by clicking the logo while on home).
  const restartIntro = useCallback(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    introTimers.current.forEach(clearTimeout);
    introTimers.current = [];
    emitNav(0);
    emitReveal(false);
    const hv = heroVideoRef.current;
    if (hv) { try { hv.pause(); hv.currentTime = 0; } catch (e) {} }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    setPhase("intro");
    setIntroRunId((n) => n + 1);
  }, []);

  useEffect(() => {
    const onRestart = () => restartIntro();
    window.addEventListener("kv:hero-restart", onRestart);
    return () => window.removeEventListener("kv:hero-restart", onRestart);
  }, [restartIntro]);

  // Preload the second (scrubbed) video once; honour reduced motion by skipping the intro.
  useEffect(() => {
    const hv = heroVideoRef.current;
    if (hv) { try { hv.load(); hv.pause(); hv.currentTime = 0; } catch (e) {} }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setPhase("hero"); emitNav(1); }
  }, []);

  // Runs the intro sequence on first mount and on every replay (introRunId bump).
  // NOTE: must NOT depend on `phase` — the internal setPhase("black") would otherwise tear down the timers.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (phaseRef.current === "hero") return;

    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);

    const iv = introVideoRef.current;
    if (iv) { try { iv.currentTime = 0; iv.play().catch(() => {}); } catch (e) {} }
    if (logoRef.current) logoRef.current.style.opacity = "0";
    const rl = requestAnimationFrame(() => { if (logoRef.current) logoRef.current.style.opacity = "1"; });

    const t1 = setTimeout(() => setPhase("black"), INTRO_MS);
    const t2 = setTimeout(() => { setPhase("hero"); window.scrollTo(0, 0); }, INTRO_MS + FADE_MS);
    introTimers.current = [t1, t2];
    return () => { cancelAnimationFrame(rl); clearTimeout(t1); clearTimeout(t2); };
  }, [introRunId]);

  // Scroll-driven, sequenced copy reveal (Terminal-Industries pacing) + site reveal event.
  useEffect(() => {
    let raf, lastReveal = false, lastNav = -1;
    const setLine = (el, o) => { if (el) { el.style.opacity = String(o); el.style.transform = `translateY(${(1 - o) * 22}px)`; } };
    const tick = () => {
      const el = rootRef.current;
      if (el && phaseRef.current === "hero") {
        const r = el.getBoundingClientRect();
        const total = r.height - window.innerHeight;
        const p = total > 0 ? clamp(-r.top / total, 0, 1) : 0;

        // Scrub the hero video by scroll position (native quality, no re-encode).
        const hv = heroVideoRef.current;
        if (hv && hv.readyState >= 1 && hv.duration && !hv.seeking) {
          const t = clamp(p, 0, 1) * (hv.duration - 0.05);
          if (Math.abs(hv.currentTime - t) > 0.033) { try { hv.currentTime = t; } catch (e) {} }
        }

        if (promptRef.current) promptRef.current.style.opacity = String(1 - clamp(p / 0.03, 0, 1));

        setLine(kickerRef.current, clamp((p - 0.02) / 0.10, 0, 1));
        setLine(headlineRef.current, clamp((p - 0.10) / 0.12, 0, 1));
        setLine(bodyRef.current, clamp((p - 0.22) / 0.12, 0, 1));
        setLine(ctaRef.current, clamp((p - 0.32) / 0.12, 0, 1));

        // Header slowly appears as you scroll into the hero.
        const navOp = clamp((p - 0.04) / 0.26, 0, 1);
        if (Math.abs(navOp - lastNav) > 0.01) { lastNav = navOp; emitNav(navOp); }

        const revealed = p > 0.72;
        if (revealed !== lastReveal) { lastReveal = revealed; emitReveal(revealed); }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); emitReveal(true); emitNav(1); };
  }, []);

  return (
    <>
      <style>{SCOPED_CSS}</style>

      {/* ---------------- STAGE ONE: cinematic logo intro ---------------- */}
      {phase !== "hero" && (
        <div className="fixed inset-0 z-[60] bg-black overflow-hidden" data-testid="home-hero-intro">
          <video
            ref={introVideoRef}
            src={INTRO_SRC}
            poster={INTRO_POSTER}
            muted
            playsInline
            preload="auto"
            data-testid="home-intro-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.35))" }} aria-hidden />
          <div
            ref={logoRef}
            style={{ opacity: 0, transition: "opacity 1.8s cubic-bezier(.22,1,.36,1)" }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-8"
            data-testid="home-hero-logo"
          >
            <div className="kv-hero-glow pointer-events-none">
              <Logo variant="stacked" tone="dark" linked={false} className="h-16 md:h-24 w-auto" />
            </div>
            <button
              type="button"
              onClick={skipIntro}
              data-testid="home-intro-enter"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-white/55 hover:text-white/90 transition-colors duration-300"
            >
              Enter
            </button>
          </div>
          {/* fade-to-black veil */}
          <div
            className="absolute inset-0 bg-black pointer-events-none"
            style={{ opacity: phase === "black" ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}
            aria-hidden
          />
        </div>
      )}

      {/* ---------------- STAGE TWO: hero background video + scroll copy ---------------- */}
      <section ref={rootRef} className="relative bg-black" style={{ height: `${PIN_VH}vh` }} data-testid="home-video-hero">
        <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
          <video
            ref={heroVideoRef}
            src={HERO_SRC}
            poster={HERO_POSTER}
            muted
            playsInline
            preload="auto"
            data-testid="home-hero-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: OVERLAY }} aria-hidden />

          {/* seamless fade from black into the second video */}
          <div
            className="absolute inset-0 bg-black pointer-events-none"
            style={{ opacity: phase === "hero" ? 0 : 1, transition: `opacity ${FADE_MS}ms ease` }}
            aria-hidden
          />

          {/* Sequenced hero copy */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="max-w-3xl">
              <p ref={kickerRef} style={{ opacity: 0 }} className="font-mono text-xs uppercase tracking-[0.26em] text-white/70 mb-5">
                Business, reengineered.
              </p>
              <h1 ref={headlineRef} style={{ opacity: 0 }} className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.04] text-white text-balance">
                Build a company that works smarter.
              </h1>
              <p ref={bodyRef} style={{ opacity: 0 }} className="mt-6 font-body text-base md:text-lg text-white/75 leading-relaxed max-w-xl mx-auto">
                Kratos Venture Group combines business development, automation, and intelligent systems to help companies grow, operate, and compete at a higher level.
              </p>
              <div ref={ctaRef} style={{ opacity: 0 }} className="mt-9 flex flex-wrap gap-4 justify-center">
                <Link to="/mythoros" data-testid="home-hero-primary-cta" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide bg-white text-black border border-white hover:bg-[#e6e8ec] transition-all duration-300">
                  Explore Mythoros©<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/contact" data-testid="home-hero-secondary-cta" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide bg-transparent text-white border border-white/40 hover:border-white transition-all duration-300">
                  Book a Strategy Call
                </Link>
              </div>
            </div>
          </div>

          {/* Scroll prompt */}
          <div ref={promptRef} className="absolute bottom-9 inset-x-0 flex justify-center pointer-events-none" data-testid="home-hero-scroll-prompt">
            <span className="kv-hero-breathe font-mono text-[11px] uppercase tracking-[0.24em] text-white/70">
              Scroll down to continue
            </span>
          </div>
        </div>
      </section>
    </>
  );
}
